import {
  bulkRemoveEventDriversAction,
  changeEventAttendanceAction,
  removeEventAttendeeAction,
} from '@/actions/event-attendees'
import OverviewDriversTable from '@/components/_championship/tables/overview-drivers/overview-drivers-table'
import ManageDriverReasonDialog from '@/components/dialogs/manage-driver-reason-dialog'
import FormSearchInput from '@/components/forms/inputs/form-search-input'
import FormPagination from '@/components/forms/pagination/form-pagination'
import HelmetIcon from '@/components/icons/helmet-icon'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { downloadCsvExport } from '@/lib/csv-export'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import useSWR from 'swr'
import ExportToCSV from '../actions/export-to-csv'
import { enrichEventAttendeesWithChampionshipDetails } from './event-attendee-details'

export default function ManageChampionshipEventDrivers({
  event,
  championshipAttendees,
  gridPositions,
  gridPositionsLabel,
  isGridUnlimited,
}) {
  const [kickReasonModalOpen, setKickReasonModalOpen] = useState(false)
  const [bulkRemoveModalOpen, setBulkRemoveModalOpen] = useState(false)

  const form = useForm({
    defaultValues: {
      pageLimit: 10,
      pageNum: 1,
      search: '',
      sortBy: JSON.stringify({
        createTs: 'desc',
      }),
    },
  })

  const [pageLimit, pageNum, sortBy, search, selectedAttendee, selectedReason] =
    form.watch([
      'pageLimit',
      'pageNum',
      'sortBy',
      'search',
      'selectedAttendee',
      'selectedReason',
    ])

  const {
    data: attendeesData,
    isLoading,
    mutate,
  } = useSWR(
    {
      url: `community/${event.championship.communityId}/championship/${event.championshipId}/event/${event.id}/attendee`,
      searchParams: {
        where: {
          status: 'registered',
          ...(search && {
            user: {
              OR: [
                {
                  firstName: {
                    contains: search,
                    mode: 'insensitive',
                  },
                },
                {
                  lastName: {
                    contains: search,
                    mode: 'insensitive',
                  },
                },
                {
                  username: {
                    contains: search,
                    mode: 'insensitive',
                  },
                },
              ],
            },
          }),
        },
        include: {
          user: true,
        },
        orderBy: sortBy ? JSON.parse(sortBy) : undefined,
        take: pageLimit,
        skip: (pageNum - 1) * pageLimit,
        includeTotal: true,
      },
    },
    apiFetch,
  )

  useEffect(() => {
    if (!attendeesData) return
    form.setValue('total', attendeesData.total)
  }, [attendeesData])

  useEffect(() => form.setValue('pageNum', 1), [search])

  const eventAttendees = useMemo(() => {
    if (!attendeesData) return []

    return enrichEventAttendeesWithChampionshipDetails(
      attendeesData.attendees,
      championshipAttendees,
    )
  }, [attendeesData, championshipAttendees])

  const eventHasSpace =
    isGridUnlimited || gridPositions - eventAttendees.length > 0

  useEffect(() => {
    if (selectedAttendee && selectedReason) {
      setKickReasonModalOpen(true)
    }
  }, [selectedAttendee, selectedReason])

  const updateAttendee = async (attendee, data) => {
    toast.loading('Updating attendee status...', { id: 'update' })
    try {
      if (data.status === 'kicked' || data.status === 'banned') {
        const result = await removeEventAttendeeAction(
          event.id,
          attendee.userId,
          data.status,
          data.reason,
        )
        if (!result.ok) throw new Error(result.message || 'Failed to update')
        await mutate()
        toast.success('Attendee status updated!', { id: 'update' })
        return
      }

      // Map the status/role from old format to a named intent
      let intent = 'attending'
      if (data.status === 'withdrawn') {
        intent = 'withdrawn'
      } else if (data.role === 'reserve') {
        intent = 'reserve'
      }

      const result = await changeEventAttendanceAction(
        event.id,
        attendee.userId,
        intent,
      )

      if (!result.ok) throw new Error(result.message || 'Failed to update')

      await mutate()
      toast.success('Attendee status updated!', { id: 'update' })
    } catch (e) {
      toast.error('Failed to update attendee status: ' + e.message, {
        id: 'update',
      })
    }
  }

  /**
   * Exports event attendees to CSV and triggers download
   * Downloads a CSV file containing driver information including:
   * - Driver name, status, and contact details
   * - Team assignment (if teams enabled)
   * - Split assignment (if splits enabled)
   * - Car class information
   * - Connected accounts (Steam, iRacing, EA, etc.)
   */
  async function exportToCSV() {
    try {
      toast.loading('Generating CSV export...', { id: 'csv-export' })
      const csvData = await ExportToCSV(event.id)

      if (!csvData || !csvData.rows || csvData.rows.length === 0) {
        toast.error('No data available to export', { id: 'csv-export' })
        return
      }

      downloadCsvExport(csvData, 'event-attendees.csv')
      toast.success(`Exported ${csvData.rows.length} drivers to CSV`, {
        id: 'csv-export',
      })
    } catch (error) {
      console.error('Failed to export CSV:', error)
      toast.error(`Failed to export CSV: ${error.message}`, {
        id: 'csv-export',
      })
    }
  }

  /**
   * Removes all registered drivers from the event
   * Sets all registered attendees to 'kicked' status with a reason
   */
  async function removeAllDrivers(reason) {
    toast.loading('Removing all drivers...', { id: 'bulk-remove' })
    try {
      const result = await bulkRemoveEventDriversAction(event.id, reason)

      if (!result.ok) throw new Error(result.message || 'Failed to remove')

      await mutate()
      toast.success(`${result.count ?? 0} drivers removed from the event`, {
        id: 'bulk-remove',
      })
      setBulkRemoveModalOpen(false)
    } catch (e) {
      toast.error('Failed to remove drivers: ' + e.message, {
        id: 'bulk-remove',
      })
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(() => {})}
        className="flex flex-col gap-5"
      >
        <div className="flex justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h4 className="text-h-4 font-semibold">Drivers Registered</h4>
            <div
              className={cn(
                'flex h-[40px] w-min items-center gap-1 text-nowrap rounded-full border border-neutral-blue-600 bg-neutral-blue-700 px-4 text-p-1 font-medium',
                {
                  'border-success-500 bg-success-500 text-black': eventHasSpace,
                  'border-error-500 bg-error-500 text-black': !eventHasSpace,
                },
              )}
            >
              <HelmetIcon className="stroke-3 size-5 stroke-black" />
              <span>
                {eventHasSpace ? 'Open: ' : 'Full: '}
                {event._count.attendees} / {gridPositionsLabel} Drivers
              </span>
            </div>
          </div>

          {eventAttendees.length > 0 && (
            <div className="flex gap-2">
              <Button type="button" onClick={exportToCSV}>
                Export to CSV
              </Button>

              <Button
                type="button"
                variant="destructive"
                onClick={() => setBulkRemoveModalOpen(true)}
              >
                Remove All Drivers
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col">
              <h5 className="text-h-5 font-semibold">Registered Drivers</h5>

              {attendeesData && (
                <span className="text-xs text-neutral-blue-400">
                  Showing {attendeesData.attendees.length} of{' '}
                  {attendeesData.total} drivers
                </span>
              )}
            </div>

            <FormSearchInput placeholder="Search drivers..." {...form} />
            {/* TODO - Add Sort by, and team filter */}
            {/* <FormSelectInput
              name="sortBy"
              labelPrefix="Sort by: "
              btnClassName="border-none bg-transparent! p-0!"
              options={[
                {
                  label: 'Most Recent',
                  value: JSON.stringify({
                    createTs: 'desc',
                  }),
                },
              ]}
              {...form}
            /> */}
          </div>

          <div className="divide"></div>

          <OverviewDriversTable
            attendees={eventAttendees}
            isLoading={isLoading}
            {...form}
            isEventPage
          />

          <FormPagination {...form} />
        </div>

        <ManageDriverReasonDialog
          open={kickReasonModalOpen}
          setOpen={() => setKickReasonModalOpen(false)}
          reasonType={selectedReason}
          member={selectedAttendee}
          setMember={(val) => form.setValue('selectedAttendee', val)}
          handleSubmit={(reason) =>
            updateAttendee(selectedAttendee, {
              status: selectedReason === 'ban' ? 'banned' : 'kicked',
              reason,
              metadata: {
                ...(selectedAttendee.metadata || {}),
                ...(selectedReason === 'kick' ? { kickReason: reason } : {}),
                ...(selectedReason === 'ban' ? { banReason: reason } : {}),
              },
            })
          }
        />

        <ManageDriverReasonDialog
          open={bulkRemoveModalOpen}
          setOpen={() => setBulkRemoveModalOpen(false)}
          reasonType="bulk-kick"
          member={{
            user: { username: `All ${attendeesData?.total || 0} Drivers` },
          }}
          setMember={() => {}}
          handleSubmit={removeAllDrivers}
        />
      </form>
    </Form>
  )
}
